import { Device, Trip, User } from "@fairfleet/geotab";
import Box from "@mui/material/Box";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { useState } from "react";
import { LogbookExcelExportButton } from "@/components/grid/LogbookExcelExportButton";
import { FilterStackLayout } from "@/components/layout/FilterStackLayout";
import SettingsPopup from "@/components/logbook/SettingsPopup";
import { useFilteredTrips } from "@/hooks/logbook/useFilteredTrips";
import { usePersistedDateRange } from "@/hooks/userSettings/usePersistedDateRange";
import { mapLazy } from "@/utilities/mapLazy";

const TripsGrid = mapLazy("TripsGrid", import("./components/TripsGrid"));
const ReasonInput = mapLazy("ReasonInput", import("@/components/logbook/ReasonInput"));
const DateRangeSelect = mapLazy("DateRangeSelect", import("@/components/input/DateRangeSelect"));
const DriverOrDeviceSelect = mapLazy(
  "DriverOrDeviceSelect",
  import("@/components/input/DriverOrDeviceSelect"),
);
const ReportDownloadButton = mapLazy(
  "ReportDownloadButton",
  import("./components/ReportDownloadButton"),
);

export default function LogbookEditPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [reasons, setReasons] = useState<string[]>([]);
  const [onlyDrivers, setOnlyDrivers] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [vehicleStatus, setVehicleStatus] = useState(true);

  // This page used to store its range under the fault overview's keys, so changing the range
  // here silently changed it there and the other way round. It now has its own, per user.
  const { fromDate, toDate, setFromDate, setToDate } = usePersistedDateRange({
    scope: "logbookEdit.dateRange",
    defaultLookbackDays: 1,
  });

  const [selectedTrips, setSelectedTrips] = useState<Trip[]>([]);
  const gridApiRef = useGridApiRef();

  const { data: trips } = useFilteredTrips({
    users,
    devices,
    toDate,
    fromDate,
    reasons,
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(600));

  return (
    <FilterStackLayout
      filterMaxWidth={1512}
      isMobile={isMobile}
      filters={
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "center",
            width: "100%",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "row", width: "100%" }}>
            <SettingsPopup
              onlyDrivers={onlyDrivers}
              setOnlyDrivers={setOnlyDrivers}
              showArchived={showArchived}
              setShowArchived={setShowArchived}
              vehicleStatus={vehicleStatus}
              setVehicleStatus={setVehicleStatus}
            />
            <DriverOrDeviceSelect
              users={users}
              devices={devices}
              onChange={(users: User[], devices: Device[]) => {
                setUsers(users ?? []);
                setDevices(devices ?? []);
              }}
              onlyDrivers={onlyDrivers}
              showArchived={showArchived}
            />
          </Box>
          <ReasonInput
            onChange={setReasons}
          />
          <DateRangeSelect
            toDate={toDate}
            fromDate={fromDate}
            onChange={(fromDate: Date, toDate: Date) => {
              setToDate(toDate ?? new Date());
              setFromDate(fromDate ?? new Date());
            }}
          />
          <ReportDownloadButton
            fromDate={fromDate}
            toDate={toDate}
            deviceIds={devices.map(d => d.id)}
            userIds={users.map(u => u.id)}
          />
          <LogbookExcelExportButton
            apiRef={gridApiRef}
            disabled={!trips || trips.length === 0}
          />
        </Box>
      }
      content={
        <TripsGrid
          apiRef={gridApiRef}
          trips={trips}
          selectedTrips={selectedTrips}
          onSelectedTripsChange={setSelectedTrips}
        />
      }
    />
  );
}
